import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import MenuBar from "../MenuBar";
import { apiFetch } from "../api";

import { useI18n } from "../i18n/I18nContext";

/* ============================================================
   THÈMES
   ============================================================ */
const THEMES = {
  dark: {
    page:          { bg: "#0a0a0a",                     color: "#fff" },
    card:          { bg: "rgba(255,255,255,0.03)",       border: "rgba(255,255,255,0.08)" },
    cardHeader:    { bg: "rgba(230,255,62,0.07)",        border: "rgba(230,255,62,0.15)",  color: "#e6ff3e" },
    row:           { border: "rgba(255,255,255,0.05)" },
    cellLabel:     { color: "#64748b" },
    input:         { bg: "rgba(255,255,255,0.05)",       border: "rgba(255,255,255,0.1)",  color: "#fff" },
    inputReadonly: { bg: "rgba(255,255,255,0.02)",       border: "rgba(255,255,255,0.06)", color: "#64748b" },
    select:        { bg: "rgba(255,255,255,0.05)",       border: "rgba(255,255,255,0.1)",  color: "#fff" },
    importBox:     { bg: "rgba(255,255,255,0.02)",       border: "rgba(96,165,250,0.35)" },
    importBtn:     { bg: "rgba(96,165,250,0.15)",        border: "rgba(96,165,250,0.4)",   color: "#60a5fa" },
    importBtnIcon: "#60a5fa",
    selectorLabel: { color: "#94a3b8" },
    selectorBox:   { bg: "rgba(255,255,255,0.05)",       border: "rgba(255,255,255,0.1)",  color: "#fff" },
    actionsPanel:  { bg: "rgba(0,0,0,0.2)",              border: "rgba(255,255,255,0.06)" },
    sectionTitle:  { color: "#fff" },
    toggleBtn:     { bg: "rgba(255,255,255,0.06)",       border: "rgba(255,255,255,0.12)", color: "#fff" },
    accent:        "#e6ff3e",
    accentText:    "#000",
    sectionLabel:  { color: "#64748b" },
    badge: (n)   => n > 0
      ? { bg: "rgba(74,222,128,0.12)", border: "rgba(74,222,128,0.3)", color: "#4ade80" }
      : { bg: "rgba(255,255,255,0.05)", border: "rgba(255,255,255,0.1)", color: "#64748b" },
  },
  light: {
    page:          { bg: "#f1f5f9",    color: "#0f172a" },
    card:          { bg: "#fff",       border: "#e2e8f0" },
    cardHeader:    { bg: "#f8fafc",    border: "#e2e8f0",  color: "#475569" },
    row:           { border: "#f1f5f9" },
    cellLabel:     { color: "#94a3b8" },
    input:         { bg: "#f8fafc",    border: "#e2e8f0",  color: "#0f172a" },
    inputReadonly: { bg: "#f1f5f9",    border: "#e2e8f0",  color: "#94a3b8" },
    select:        { bg: "#f8fafc",    border: "#e2e8f0",  color: "#0f172a" },
    importBox:     { bg: "#eff6ff",    border: "#93c5fd" },
    importBtn:     { bg: "#dbeafe",    border: "#93c5fd",  color: "#1d4ed8" },
    importBtnIcon: "#1d4ed8",
    selectorLabel: { color: "#475569" },
    selectorBox:   { bg: "#fff",       border: "#e2e8f0",  color: "#0f172a" },
    actionsPanel:  { bg: "#f8fafc",    border: "#e2e8f0" },
    sectionTitle:  { color: "#0f172a" },
    toggleBtn:     { bg: "#fff",       border: "#e2e8f0",  color: "#334155" },
    accent:        "#1d4ed8",
    accentText:    "#fff",
    sectionLabel:  { color: "#94a3b8" },
    badge: (n)   => n > 0
      ? { bg: "#dcfce7", border: "#86efac", color: "#166534" }
      : { bg: "#f1f5f9", border: "#e2e8f0", color: "#94a3b8" },
  },
};

/* ============================================================
   INJECT CSS DYNAMIQUE
   ============================================================ */
function injectStyles(mode) {
  let el = document.getElementById("ilss-dynamic-styles");
  if (!el) { el = document.createElement("style"); el.id = "ilss-dynamic-styles"; document.head.appendChild(el); }

  const lime        = mode === "dark" ? "rgba(230,255,62,0.5)"   : "#1d4ed8";
  const limeShadow  = mode === "dark" ? "rgba(230,255,62,0.1)"   : "rgba(29,78,216,0.1)";
  const limeBg      = mode === "dark" ? "rgba(230,255,62,0.04)"  : "rgba(29,78,216,0.04)";
  const btnHover    = mode === "dark" ? "#fff"                    : "#2563eb";
  const btnShadow   = mode === "dark" ? "rgba(230,255,62,0.3)"   : "rgba(29,78,216,0.3)";
  const btnHoverTxt = mode === "dark" ? "#000"                    : "#fff";

  el.innerHTML = `
    .ilss-input:focus {
      border-color: ${lime} !important;
      box-shadow: 0 0 0 3px ${limeShadow} !important;
      background: ${limeBg} !important;
    }
    .ilss-select:focus { border-color: ${lime} !important; box-shadow: 0 0 0 3px ${limeShadow} !important; }
    .ilss-btn-save:hover:not(:disabled) {
      background: ${btnHover} !important;
      color: ${btnHoverTxt} !important;
      transform: translateY(-1px) !important;
      box-shadow: 0 8px 24px ${btnShadow} !important;
    }
    .ilss-btn-save:active  { transform: scale(0.98) !important; }
    .ilss-btn-save:disabled { opacity: 0.5; cursor: not-allowed; }
    .ilss-import-label:hover { opacity: 0.85; transform: translateY(-1px); }
    .ilss-toggle-btn:hover  { opacity: 0.8; transform: scale(1.05); }
    @media (min-width: 640px)  { .ilss-wrap { padding: 24px 32px 48px !important; } .ilss-row { grid-template-columns: 180px 1fr !important; } }
    @media (min-width: 900px)  { .ilss-wrap { padding: 28px 60px 48px !important; } .ilss-layout { grid-template-columns: 1fr 300px !important; } }
    @media (min-width: 1200px) { .ilss-wrap { padding: 32px 140px 48px !important; } }
    @media (max-width: 480px)  { .ilss-row { grid-template-columns: 1fr !important; } }
  `;
}

/* ============================================================
   COMPOSANT PRINCIPAL
   ============================================================ */
export default function ILSSForm() {
  const { t, ui } = useI18n();

  const location = useLocation();
  const navigate = useNavigate();
  const today    = new Date().toISOString().split("T")[0];

  /* ── Thème ── */
  const [mode, setMode] = useState(() => localStorage.getItem("qsf-theme") || "dark");
  const T = THEMES[mode];
  useEffect(() => { injectStyles(mode); localStorage.setItem("qsf-theme", mode); }, [mode]);
  const toggleMode = () => setMode((m) => m === "dark" ? "light" : "dark");

  const [operator, setOperator]             = useState("");
  const [message, setMessage]               = useState({ text: "", ok: true });
  const [submitting, setSubmitting]         = useState(false);
  const [editing, setEditing]               = useState(false);
  const [csvData, setCsvData]               = useState([]);
  const [selectedTestIndex, setSelectedTestIndex] = useState("");

  const initialFormData = {
    Date: today, Operator: "", ID_Stack: "", No_Test: "",
    Typologie: "", Largeur: "", Epaisseur: "", Force: "",
    Typ_Rupt: "", Categorie: "",
  };
  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    const raw = localStorage.getItem("user");
    if (raw) { try { const u = JSON.parse(raw); setOperator(u.username || ""); } catch {} }
  }, []);
  useEffect(() => { if (operator) setFormData((p) => ({ ...p, Operator: operator })); }, [operator]);
  useEffect(() => {
    const fromState = location.state?.item;
    if (fromState) { setFormData((p) => ({ ...p, ...fromState })); setEditing(true); }
  }, [location.state]);

  /* ── Parser Excel ── */
  const parseExcelOrCSV = (workbook) => {
    const tests = [];
    const sheetName = workbook.SheetNames[0];
    const rawData   = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { header: 1, defval: "" });
    let testDate = today;

    rawData.forEach((row) => {
      const rowStr = row.join(" ");
      if (rowStr.includes("Test Date")) {
        const match = rowStr.match(/Test Date\s*:\s*([^\s]+)/);
        if (match) {
          const [day, month, year] = match[1].trim().split("/");
          if (day && month && year)
            testDate = `${year}-${month.padStart(2,"0")}-${day.padStart(2,"0")}`;
        }
      }
    });

    let s1 = -1;
    for (let i = 0; i < rawData.length; i++) {
      if (rawData[i].join(" ").toLowerCase().includes("test no") && rawData[i].join(" ").toLowerCase().includes("référence")) { s1 = i + 1; break; }
    }
    if (s1 === -1) return [];

    for (let i = s1; i < rawData.length; i++) {
      const row    = rawData[i];
      const testNo = String(row[0] || "").trim();
      if (testNo.match(/^(Min|Mean|Max|S\.D\.|C\. of V\.|L\.C\.L\.|U\.C\.L\.)$/i)) break;
      if (testNo.match(/^\d+$/)) {
        const ref = String(row[1] || "").trim();
        let idStack = "", testNumber = testNo;
        if (ref.includes("-")) { const p = ref.split("-"); idStack = p[0]; testNumber = p[1]; }
        else idStack = ref;
        tests.push({ testNoFromExcel: testNo, Date: testDate, Operator: operator, ID_Stack: idStack, No_Test: testNumber, Typologie: "", Largeur: String(row[7] || "").trim(), Epaisseur: "", Force: "", Typ_Rupt: String(row[4] || "").trim(), Categorie: String(row[2] || "").trim() });
      }
    }
    if (!tests.length) return [];

    let s2 = -1;
    for (let i = 0; i < rawData.length; i++) {
      if (rawData[i].join(" ").toLowerCase().includes("test no") && rawData[i].join(" ").toLowerCase().includes("thickness")) { s2 = i + 1; break; }
    }
    if (s2 !== -1) {
      for (let i = s2; i < rawData.length; i++) {
        const row    = rawData[i];
        const testNo = String(row[0] || "").trim();
        if (testNo.match(/^(Min|Mean|Max|S\.D\.)$/i)) break;
        if (testNo.match(/^\d+$/)) {
          const idx = tests.findIndex((t) => t.testNoFromExcel === testNo);
          if (idx !== -1) { tests[idx].Epaisseur = String(row[1] || ""); tests[idx].Force = String(row[2] || ""); }
        }
      }
      tests.forEach((t) => delete t.testNoFromExcel);
    }
    return tests;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const wb     = XLSX.read(new Uint8Array(ev.target.result), { type: "array" });
        const parsed = parseExcelOrCSV(wb);
        if (parsed.length > 0) { setCsvData(parsed); setMessage({ text: `${parsed.length} test(s) chargé(s)`, ok: true }); }
        else setMessage({ text: "Aucun test trouvé dans le fichier", ok: false });
      } catch { setMessage({ text: "Erreur de lecture du fichier", ok: false }); }
    };
    reader.onerror = () => setMessage({ text: "Erreur de lecture du fichier", ok: false });
    reader.readAsArrayBuffer(file);
  };

  const handleTestSelection = (e) => {
    const index = e.target.value;
    setSelectedTestIndex(index);
    if (index !== "" && csvData[index]) {
      setFormData(csvData[index]);
      setMessage({ text: `Test ${csvData[index].No_Test} – ${csvData[index].ID_Stack} chargé`, ok: true });
    } else {
      setFormData({ ...initialFormData, Operator: operator });
      setMessage({ text: "", ok: true });
    }
  };

  const handleChange = (e) => {
    let { name, value, type } = e.target;
    if (type === "number" && Number(value) < 0) value = "";
    setFormData((s) => ({ ...s, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSubmitting(true);
    setMessage({ text: "", ok: true });
    try {
      const r      = await apiFetch(editing ? "/api/ilss/update" : "/api/ilss/add", {
        method:  editing ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(formData),
      });
      const result = await r.json();
      setMessage({ text: result.message || "Terminé", ok: result.status === "success" });

      if (result.status === "success" && !editing) {
        const updated = csvData.filter((_, i) => i !== parseInt(selectedTestIndex));
        setCsvData(updated);
        setTimeout(() => {
          setMessage({ text: updated.length > 0 ? `${updated.length} test(s) restant(s)` : "🎉 Tous les tests sauvegardés !", ok: true });
        }, 900);
        setFormData({ Date: new Date().toISOString().split("T")[0], Operator: operator, ID_Stack: "", No_Test: "", Typologie: "", Largeur: "", Epaisseur: "", Force: "", Typ_Rupt: "", Categorie: "" });
        setSelectedTestIndex("");
      }
    } catch { setMessage({ text: "Erreur réseau", ok: false }); }
    finally  { setSubmitting(false); }
  };

  /* ── styles thémés inline ── */
  const s = {
    page:          { minHeight: "100vh", background: T.page.bg, color: T.page.color, fontFamily: "system-ui,-apple-system,Segoe UI,Roboto,sans-serif", transition: "background 0.3s,color 0.3s" },
    wrap:          { padding: "20px 16px 48px" },
    topBar:        { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, marginTop: 8 },
    sectionLeft:   { display: "flex", alignItems: "center", gap: 12 },
    sectionBadge:  { background: T.accent, color: T.accentText, fontWeight: 900, fontSize: 11, letterSpacing: "0.08em", textTransform: "uppercase", padding: "4px 12px", borderRadius: 4 },
    sectionTitle:  { fontWeight: 900, fontSize: 20, letterSpacing: "0.04em", textTransform: "uppercase", color: T.sectionTitle.color },
    toggleBtn:     { display: "flex", alignItems: "center", gap: 8, background: T.toggleBtn.bg, border: `1px solid ${T.toggleBtn.border}`, color: T.toggleBtn.color, borderRadius: 10, padding: "8px 14px", cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit", transition: "all 0.2s" },

    card:          { background: T.card.bg, border: `1px solid ${T.card.border}`, borderRadius: 16, overflow: "hidden", boxShadow: "0 8px 32px rgba(0,0,0,0.15)", transition: "all 0.3s" },
    cardHeader:    { padding: "14px 20px", background: T.cardHeader.bg, borderBottom: `1px solid ${T.cardHeader.border}`, fontWeight: 800, fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", color: T.cardHeader.color },
    layout:        { display: "grid", gridTemplateColumns: "1fr" },
    formWrap:      { padding: "16px 20px 20px" },

    /* ── Import ── */
    importBox:     { background: T.importBox.bg, border: `2px dashed ${T.importBox.border}`, borderRadius: 12, padding: 20, marginBottom: 20, transition: "all 0.3s" },
    importTopRow:  { display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12 },
    importLabel:   { display: "inline-flex", alignItems: "center", gap: 8, padding: "10px 18px", background: T.importBtn.bg, border: `1px solid ${T.importBtn.border}`, color: T.importBtn.color, fontWeight: 700, borderRadius: 8, cursor: "pointer", fontSize: 13, fontFamily: "inherit", transition: "all 0.2s" },
    importBadge:   (n) => ({ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", borderRadius: 20, fontSize: 12, fontWeight: 700, background: T.badge(n).bg, border: `1px solid ${T.badge(n).border}`, color: T.badge(n).color }),
    selectorWrap:  { marginTop: 14, display: "flex", flexDirection: "column", gap: 6 },
    selectorLabel: { fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: T.selectorLabel.color },
    select:        { width: "100%", height: 40, background: T.selectorBox.bg, border: `1px solid ${T.selectorBox.border}`, borderRadius: 8, color: T.selectorBox.color, padding: "0 10px", fontSize: 13, fontFamily: "inherit", outline: "none" },

    /* ── Form ── */
    row:           { display: "grid", gridTemplateColumns: "160px 1fr", alignItems: "center", borderBottom: `1px solid ${T.row.border}`, minHeight: 50 },
    cellLabel:     { fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: T.cellLabel.color, paddingRight: 12, paddingLeft: 4 },
    cellInput:     { padding: "7px 0" },
    input:         { width: "100%", height: 36, background: T.input.bg, border: `1px solid ${T.input.border}`, borderRadius: 8, color: T.input.color, fontSize: 13, fontWeight: 600, paddingLeft: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box", transition: "all 0.2s" },
    inputReadonly: { width: "100%", height: 36, background: T.inputReadonly.bg, border: `1px solid ${T.inputReadonly.border}`, borderRadius: 8, color: T.inputReadonly.color, fontSize: 13, fontWeight: 700, paddingLeft: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box", letterSpacing: "0.04em" },
    inputSelect:   { width: "100%", height: 36, background: T.select.bg, border: `1px solid ${T.select.border}`, borderRadius: 8, color: T.select.color, fontSize: 13, fontWeight: 600, paddingLeft: 10, outline: "none", fontFamily: "inherit", boxSizing: "border-box" },

    /* ── Actions ── */
    actionsPanel:  { borderTop: `1px solid ${T.actionsPanel.border}`, padding: 20, display: "flex", flexDirection: "column", gap: 14, background: T.actionsPanel.bg },
    btnSave:       { width: "100%", padding: "15px 24px", background: T.accent, color: T.accentText, fontWeight: 900, fontSize: 14, letterSpacing: "0.06em", textTransform: "uppercase", border: "none", borderRadius: 10, cursor: "pointer", fontFamily: "inherit", transition: "all 0.2s" },
    msgSuccess:    { textAlign: "center", fontWeight: 700, fontSize: 13, color: "#4ade80", padding: "10px 14px", background: "rgba(74,222,128,0.08)", border: "1px solid rgba(74,222,128,0.2)", borderRadius: 8 },
    msgError:      { textAlign: "center", fontWeight: 700, fontSize: 13, color: "#f87171", padding: "10px 14px", background: "rgba(248,113,113,0.08)", border: "1px solid rgba(248,113,113,0.2)", borderRadius: 8 },
  };

  const RUPTURES  = ["Traction","Cisaillement","Cisaillement Multiple","Cisaillement Simple","Cisaillement Double","Cisaillement Interne","Délamination","Délaminé","Pas de Rupture", "Ruptur de la fibre en traction", "Rupture en traction de la couche la plus externe","Rupture en compression", "Rupture en traction (comprenant du cisaillment interlaminaire)" , "Rupture en compression (comprenant du cisaillement interlaminaire)", "Rupture en cisaillement interlaminaire"];
  const CATEGORIES = ["A","B","C","D"];

  return (
    <div style={s.page}>
      <MenuBar active="qualite" />

      <div style={s.wrap} className="ilss-wrap">

        {/* ── Top bar ── */}
        <div style={s.topBar}>
          <div style={s.sectionLeft}>
            <span style={s.sectionBadge}>Qualité</span>
            <span style={s.sectionTitle}>Contrôle ILS</span>
          </div>
          <button style={s.toggleBtn} className="ilss-toggle-btn" onClick={toggleMode}>
            <span style={{ fontSize: 16 }}>{mode === "dark" ? "☀️" : "🌙"}</span>
            <span>{mode === "dark" ? "Clair" : "Sombre"}</span>
          </button>
        </div>

        {/* ── Card ── */}
        <div style={s.card}>
          <div style={s.cardHeader}>Paramètres de mesure</div>

          <div style={s.layout} className="ilss-layout">
            <div style={s.formWrap}>

              {/* ── Import Excel ── */}
              <div style={s.importBox}>
                <div style={s.importTopRow}>
                  <label style={s.importLabel} className="ilss-import-label">
                    <span>📄</span>
                    <span>Importer Excel / CSV</span>
                    <input type="file" accept=".xlsx,.xls,.csv" onChange={handleFileUpload} style={{ display: "none" }} />
                  </label>
                  <div style={s.importBadge(csvData.length)}>
                    {csvData.length > 0 ? `${csvData.length} test(s) chargé(s)` : "Aucun fichier"}
                  </div>
                </div>

                {csvData.length > 0 && (
                  <div style={s.selectorWrap}>
                    <div style={s.selectorLabel}>Sélectionner un test</div>
                    <select value={selectedTestIndex} onChange={handleTestSelection} style={s.select} className="ilss-select">
                      <option value="">— Choisir un test —</option>
                      {csvData.map((test, idx) => (
                        <option key={idx} value={idx}>
                          Test {test.No_Test} · {test.ID_Stack} · Cat. {test.Categorie}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* ── Formulaire ── */}
              <form onSubmit={handleSubmit}>

                {/* Champs simples */}
                {[
                  { label: "Date",       name: "Date",     type: "date"   },
                  { label: "ID Stack",   name: "ID_Stack", type: "text",  placeholder: "T137…" },
                  { label: "N° Test",    name: "No_Test",  type: "text"   },
                  { label: "Largeur (mm)", name: "Largeur",  type: "number", step: "0.01" },
                  { label: "Épaisseur (mm)", name: "Epaisseur", type: "number", step: "0.01" },
                  { label: "Force (N)",  name: "Force",    type: "number", step: "0.1" },
                ].map(({ label, name, type, step, placeholder }) => (
                  <div key={name} style={s.row} className="ilss-row">
                    <div style={s.cellLabel}>{label}</div>
                    <div style={s.cellInput}>
                      <input
                        name={name} type={type} step={step}
                        value={formData[name]} onChange={handleChange}
                        placeholder={placeholder || ""}
                        style={s.input} className="ilss-input"
                      />
                    </div>
                  </div>
                ))}

                {/* Opérateur readonly */}
                <div style={s.row} className="ilss-row">
                  <div style={s.cellLabel}>Opérateur</div>
                  <div style={s.cellInput}>
                    <input type="text" readOnly value={operator ? operator.toUpperCase() : "NON CONNECTÉ"} style={s.inputReadonly} />
                  </div>
                </div>

                {/* Selects */}
                {[
                  { label: "Typologie",         name: "Typologie",  options: ["BDB","72h"] },
                  { label: "Type Rupture",       name: "Typ_Rupt",   options: RUPTURES },
                  { label: "Catégorie Qualité",  name: "Categorie",  options: CATEGORIES },
                ].map(({ label, name, options }) => (
                  <div key={name} style={s.row} className="ilss-row">
                    <div style={s.cellLabel}>{label}</div>
                    <div style={s.cellInput}>
                      <select name={name} value={formData[name]} onChange={handleChange} style={s.inputSelect} className="ilss-select">
                        <option value="">{ui("— Sélectionner —")}</option>
                        {options.map((o) => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </div>
                  </div>
                ))}
              </form>
            </div>

            {/* ── Actions ── */}
            <div style={s.actionsPanel}>
              <button
                style={s.btnSave}
                className="ilss-btn-save"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting
                  ? (editing ? "Mise à jour…" : "Sauvegarde…")
                  : (editing ? "✎ Mettre à jour" : "✓ Sauvegarder")}
              </button>

              {message.text && (
                <div style={message.ok ? s.msgSuccess : s.msgError}>
                  {message.ok ? "✅ " : "❌ "}{message.text}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
